import { parseLengthToken } from "../units/length.js";

export const MIN_PANEL_DIMENSION_MM = 40;

/** @typedef {"empty" | "invalidFormat" | "lengthMin" | "widthMin" | "quantityMin"} ParsePartLineError */

/**
 * @typedef {Object} ParsedPart
 * @property {number} length
 * @property {number} width
 * @property {number} quantity
 * @property {string} name
 */

/**
 * @param {string} text
 * @param {{ defaultSystem?: import("../units/length.js").UnitSystem }} [options]
 * @returns {{ dimension: string, remainder: string }}
 */
function splitDimensionAndRemainder(text, options) {
  const trimmed = String(text || "").trim();
  if (!trimmed) {
    return { dimension: "", remainder: "" };
  }

  let bestEnd = -1;
  for (let end = 1; end <= trimmed.length; end += 1) {
    const candidate = trimmed.slice(0, end).trim();
    if (!candidate) continue;
    if (parseLengthToken(candidate, options) !== null) {
      bestEnd = end;
    }
  }

  if (bestEnd < 0) {
    return { dimension: trimmed, remainder: "" };
  }

  return {
    dimension: trimmed.slice(0, bestEnd).trim(),
    remainder: trimmed.slice(bestEnd).trim(),
  };
}

/**
 * @param {string} text
 * @param {{ unitSystem?: import("../units/length.js").UnitSystem, minDimensionMm?: number }} [options]
 * @returns {{ ok: true, part: ParsedPart } | { ok: false, error: ParsePartLineError }}
 */
export function parsePartLine(text, options = {}) {
  const {
    unitSystem = "metric",
    minDimensionMm = MIN_PANEL_DIMENSION_MM,
  } = options;
  const parseOptions = { defaultSystem: unitSystem };
  const trimmed = String(text || "").trim();

  if (!trimmed) {
    return { ok: false, error: "empty" };
  }

  const segments = trimmed.split(/\s*[x×*]\s*/i);
  if (segments.length < 2) {
    return { ok: false, error: "invalidFormat" };
  }

  const length = parseLengthToken(segments[0], parseOptions);
  if (length === null) {
    return { ok: false, error: "invalidFormat" };
  }

  let widthStr = segments[1];
  let quantity = 1;
  let name = "";

  if (segments.length === 2) {
    const split = splitDimensionAndRemainder(segments[1], parseOptions);
    widthStr = split.dimension;
    name = split.remainder;
  } else {
    widthStr = segments[1];
    const tail = segments[2].trim();
    const qtyMatch = tail.match(/^(\d+)(?:\s+(.*))?$/);
    if (!qtyMatch) {
      return { ok: false, error: "invalidFormat" };
    }
    quantity = Number(qtyMatch[1]);
    name = (qtyMatch[2] || "").trim();
    if (segments.length > 3) {
      name = [name, ...segments.slice(3)].filter(Boolean).join(" x ").trim();
    }
  }

  const width = parseLengthToken(widthStr, parseOptions);
  if (width === null) {
    return { ok: false, error: "invalidFormat" };
  }

  const lengthMm = Math.round(length);
  const widthMm = Math.round(width);

  if (!Number.isFinite(lengthMm) || lengthMm < minDimensionMm) {
    return { ok: false, error: "lengthMin" };
  }
  if (!Number.isFinite(widthMm) || widthMm < minDimensionMm) {
    return { ok: false, error: "widthMin" };
  }
  if (!Number.isInteger(quantity) || quantity < 1) {
    return { ok: false, error: "quantityMin" };
  }

  return {
    ok: true,
    part: {
      length: lengthMm,
      width: widthMm,
      quantity,
      name,
    },
  };
}
